import React from 'react';

import PropTypes from 'prop-types';

import { useIndividualJobProgress } from '../../../models/jobs/queries';

import { progressCounts } from './progressCounts';

const JobProgressBar = ({ job, jobTypeName = 'Job' }) => {
	const { data: progressData, isLoading, error } = useIndividualJobProgress(job);

	// Check if job is in cancelling state (aborted but not yet finished)
	const isCancelling = job.status === 'aborted' && !job.finishedAt;
	const isCancelled = job.status === 'aborted' && Boolean(job.finishedAt);

	if (isLoading) {
		return (
			<div className="flex items-center gap-2 mb-2">
				<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-bgBase-600" />
				<span className="text-sm text-bgBase-800">Lade Job-Status...</span>
			</div>
		);
	}

	// The status endpoint answers from the durable job table even after Redis restarts,
	// so an error here is transient (network/backend down) — the job list keeps polling
	// and the job itself is unaffected. No "abgebrochen" messaging for missing Bull jobs.
	if (error) {
		const isNetworkError = error?.message?.includes('Failed to fetch') || error?.message?.includes('Network');
		const errorMessage = isNetworkError
			? 'Backend nicht erreichbar – Job-Status wird weiter abgefragt...'
			: 'Job-Status konnte nicht geladen werden.';

		return (
			<div className="bg-caution-200 border border-caution-500 text-caution-700 px-3 py-1 rounded-control mb-2">
				<span className="text-sm">{errorMessage}</span>
			</div>
		);
	}

	const state = progressData?.state || job.status;
	const attempt = progressData?.attempt ?? job.attempt ?? 0;
	let progress = progressData?.progress?.percent || 0;

	//Always set progress to 100% if job is completed (progress percentage not updated after completion)
	if (state === 'completed') progress = 100;

	// Determine progress bar color based on state
	let barColorClass = 'bg-primary-500';
	let label = jobTypeName;

	// Handle cancelling/cancelled states first (override other states)
	if (isCancelling) {
		barColorClass = 'bg-caution-500';
		label = `${jobTypeName} - Wird abgebrochen...`;
	} else if (isCancelled) {
		barColorClass = 'bg-caution-700';
		label = `${jobTypeName} - Abgebrochen`;
	} else {
		switch (state) {
			case 'queued':
			case 'waiting':
			case 'delayed':
				barColorClass = 'bg-bgBase-600';
				// attempt > 1 in the queue = a failed attempt is being retried by the backend
				label = attempt > 1 ? `${jobTypeName} - Neuer Versuch (${attempt})` : `${jobTypeName} - Warteschlange`;
				break;
			// waiting-children = a BullMQ flow parent whose per-company children are working
			case 'active':
			case 'running':
			case 'waiting-children':
				barColorClass = 'bg-primary-500';
				label = `${jobTypeName} - Läuft`;
				break;
			case 'completed':
				barColorClass = 'bg-success-500';
				label = `${jobTypeName} - Abgeschlossen`;
				break;
			case 'failed':
				barColorClass = 'bg-warning-500';
				label = `${jobTypeName} - Fehlgeschlagen`;
				break;
			case 'aborted':
				barColorClass = 'bg-caution-700';
				label = `${jobTypeName} - Abgebrochen`;
				break;
			default:
				label = `${jobTypeName} - ${state}`;
		}
	}

	// For queued jobs or cancelling jobs, show indeterminate progress
	const progressValue =
		state === 'queued' || state === 'waiting' || state === 'delayed' || isCancelling
			? undefined // Indeterminate
			: Math.max(5, progress); // Ensure at least 5% visibility

	const isAnimated =
		state === 'active' || state === 'running' || state === 'waiting-children' || state === 'queued' || isCancelling;
	const isIndeterminate = state === 'queued' || state === 'waiting' || state === 'delayed' || isCancelling;
	const isRunning = state === 'active' || state === 'running' || state === 'waiting-children';
	const counts = isRunning ? progressCounts(progressData?.progress) : null;

	return (
		<div className="mb-2">
			<div className="flex justify-between items-center mb-1">
				<span className="text-sm text-text">{label}</span>
				{isRunning && (
					<span className="text-sm text-bgBase-800">
						{counts ? `${counts} · ` : ''}
						{Math.round(progress)}%
					</span>
				)}
			</div>

			<div className="w-full bg-bgBase-500 rounded-full h-1.5 overflow-hidden">
				{isIndeterminate ? (
					/* Indeterminate progress bar with animation */
					<div
						className={`h-full ${barColorClass} animate-pulse`}
						style={{
							background: `repeating-linear-gradient(
							45deg,
							transparent,
							transparent 6px,
							rgba(255,255,255,0.3) 6px,
							rgba(255,255,255,0.3) 12px
						)`,
						}}
					/>
				) : (
					/* Determinate progress bar */
					<div
						className={`h-full transition-all duration-300 ${barColorClass} ${isAnimated ? 'animate-pulse' : ''}`}
						style={{ width: `${Math.max(5, progressValue || 0)}%` }}
					/>
				)}
			</div>
		</div>
	);
};

JobProgressBar.propTypes = {
	job: PropTypes.shape({
		id: PropTypes.string.isRequired,
		bullJobId: PropTypes.string.isRequired,
		jobType: PropTypes.string.isRequired,
		status: PropTypes.string.isRequired,
		finishedAt: PropTypes.string,
		attempt: PropTypes.number,
	}).isRequired,
	jobTypeName: PropTypes.string,
};

export default JobProgressBar;
